import { Check, MessageCircle, ArrowRight } from "lucide-react";
import { WA_LINK } from "./shared";

export default function SeoContentSection({ t }) {
  if (!t.seoBlocks) return null;
  return (
    <section className="bg-white py-16 sm:py-24 lg:py-32" data-testid="weddings-seo">
      <div className="max-w-[1200px] mx-auto px-5 sm:px-10">
        <div className="text-center mb-14 sm:mb-20">
          <p className="text-[10px] sm:text-[11px] tracking-[0.3em] sm:tracking-[0.35em] text-[#B8966F] mb-3 sm:mb-4 font-light">{t.seoEyebrow}</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl text-[#1F1815] leading-tight max-w-3xl mx-auto" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}>
            {t.seoTitle} <span className="italic text-[#8B6F47]">{t.seoTitleAccent}</span>
          </h2>
          <div className="w-12 sm:w-16 h-px bg-[#C9A87C] mx-auto mt-6 sm:mt-7" />
        </div>

        <div className="space-y-16 sm:space-y-24 lg:space-y-28 mb-16 sm:mb-20">
          {t.seoBlocks.map((b, i) => {
            const reverse = i % 2 === 1;
            return (
              <div key={i}
                   className={`grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}
                   data-testid={`weddings-seo-block-${i}`}>
                <div>
                  <h3 className="text-2xl sm:text-3xl lg:text-4xl text-[#1F1815] leading-tight mb-4 sm:mb-6"
                      style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
                    {b.heading}
                  </h3>
                  <div className="w-10 h-px bg-[#C9A87C] mb-5 sm:mb-7" />
                  <p className="text-sm sm:text-base text-[#3D2F22] font-light leading-[1.85]">{b.content}</p>
                </div>
                <div className="relative aspect-[4/5] sm:aspect-[5/6] rounded-sm overflow-hidden bg-[#FAF5EE] shadow-[0_25px_60px_-25px_rgba(31,24,21,0.35)]">
                  <img
                    src={b.image}
                    srcSet={`${b.image.replace(/\.webp$/, "-sm.webp")} 900w, ${b.image} 1400w`}
                    sizes="(max-width: 1024px) 100vw, 50vw"
                    alt={b.imageAlt}
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] hover:scale-105"
                  />
                </div>
              </div>
            );
          })}
        </div>

        <div className="bg-[#FAF5EE] rounded-sm p-7 sm:p-12 lg:p-14 mb-12 sm:mb-16">
          <h3 className="text-2xl sm:text-3xl text-[#1F1815] mb-6 sm:mb-8"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
            {t.seoServiceTitle}
          </h3>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
            {t.seoServices.map((s, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm sm:text-[15px] text-[#3D2F22] font-light leading-relaxed">
                <Check className="w-4 h-4 mt-1 shrink-0 text-[#C9A87C]" />
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="mb-12 sm:mb-16">
          <h3 className="text-2xl sm:text-3xl text-[#1F1815] mb-5 sm:mb-7 text-center"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
            {t.seoLocationsTitle}
          </h3>
          <div className="flex flex-wrap justify-center gap-2 sm:gap-2.5 max-w-3xl mx-auto">
            {t.seoLocations.map(loc => (
              <span key={loc} className="inline-block px-3.5 sm:px-4 py-1.5 sm:py-2 border border-[#EFE4D2] bg-white text-[#3D2F22] text-xs sm:text-[13px] font-light hover:border-[#C9A87C] hover:text-[#8B6F47] transition cursor-default rounded-sm">
                {loc}
              </span>
            ))}
          </div>
        </div>

        <div className="max-w-3xl mx-auto text-center border-t border-[#EFE4D2] pt-12 sm:pt-14">
          <p className="text-sm sm:text-base text-[#3D2F22] font-light leading-[1.85] mb-8 sm:mb-10">
            {t.seoFinal}
          </p>
          <a href={WA_LINK} target="_blank" rel="noopener noreferrer"
             className="inline-flex items-center gap-2 sm:gap-3 px-7 sm:px-9 py-3.5 sm:py-4 bg-[#0A0807] hover:bg-black text-[#F5C77A] text-[11px] sm:text-[12px] tracking-[0.22em] sm:tracking-[0.25em] font-medium transition-all rounded-sm hover:gap-3 sm:hover:gap-4"
             data-testid="weddings-seo-cta">
            <MessageCircle className="w-4 h-4" />
            <span>{t.seoCta}</span>
            <ArrowRight className="w-3 h-3" />
          </a>
        </div>
      </div>
    </section>
  );
}
